<template>
  <div>
    <header-page :title="'Partner statuses'" :hasBackLink="false" />

    <div class="filters-container">
      <div class="filters-block">
        <div class="search">
          <input-text v-model="search_text" />
          <div
            v-if="isShowClear"
            class="main-button lg ml-15"
            @click="clearFilters"
          >
            <div>Clear</div>
          </div>
        </div>
        <div class="button">
          <div v-if="false" class="main-button lg orange" @click="setFilters">
            <div class="icon" v-html="Filter"></div>
            <div>Filters</div>
          </div>
        </div>
      </div>
      <div class="selected-filters" v-if="selectedFilters.length">
        <div v-for="item in selectedFilters" :key="item.text">
          <div class="icon" @click="removeFilter(item.id)" v-html="Cross"></div>
          <div class="text">{{ item.text }}</div>
        </div>
      </div>
    </div>

    <grid
      v-model:columns="columns"
      v-model="rows"
      :options="options"
      :page="page"
      :per_page="per_page"
      :newRowComponent="newRowComponent"
      :isHideNewRowComponent="isHideNewRowComponent"
      :countRows="countRows"
      :search_text="search_textToGrid"
      @deleteRow="deleteRow"
      @nextPage="nextPage"
      @prevPage="prevPage"
      @saveNewRow="saveNewPartner"
      @saveRow="savePartner"
      @setPerPage="setPerPage"
      @sortRows="fetchData"
    />
    <set-filters v-if="isVisibleFilters" v-model="isVisibleFilters" />
  </div>
</template>

<script>
import { shallowRef } from 'vue'
import NewRowPartnerStatus from '@/components/NewRows/partner/NewRowPartnerStatus'
import { fetchData, urls } from '@/utils/urls.js'
import { hasAccess, permissions } from '@/utils/permissions'
import { listMixin } from '@/utils/list-mixin'
import { GrigOptions } from '@/models/grid-options'
import { GridColumn } from '@/models/grid-column'

export default {
  name: 'PartnerStatuses',
  mixins: [listMixin],

  data() {
    let colsKeyUrlIndex = 0
    return {
      options: new GrigOptions({
        isSelectable: false,
        isEditable: hasAccess(permissions.update_partner_status),
        isAdd: hasAccess(permissions.create_partner_status),
        isDelete: hasAccess(permissions.delete_partner_status),
        addButtonText: 'Add partner status',
        getDeleteContent: async (rows) => {
          if (!rows) return ``
          let query = `page=1&per_page=1`
          let links = await fetchData(
            urls.partners,
            'GET',
            null,
            query + `&status_f=${rows[0].partner_status}&partners_f=all`
          )
          let count = links?.count || 0

          return `<div class="mb-10">
            Are you sure you want to delete partner status?
              ${rows
                .map((item) => `<div>• ${item.partner_status} (${count})</div>`)
                .join('')}
            </div>`
        },
      }),

      columns: [
        new GridColumn({
          keyUrl: 's' + colsKeyUrlIndex++,
          key: 'partner_status',
          text: 'Partner status',
        }),
      ],
      newPartner: {
        partner_status: null,
      },
      newRowComponent: shallowRef(NewRowPartnerStatus),
    }
  },

  created() {
    this.setDataFromQueryParams() // 3
    this.fetchData()
  },

  methods: {
    saveNewPartner(partner) {
      this.setNewPartner(partner)
      this.addRow()
    },

    savePartner(partner) {
      this.editRow(partner)
    },

    newPartnerToDefault() {
      for (let key in this.newPartner) {
        this.newPartner[key] = null
      }
    },

    setNewPartner(partner) {
      for (let key in partner) {
        this.newPartner[key] = partner[key]
      }
    },

    async editRow(partner) {
      this.$store.commit('setIsLoading', true)
      const id = partner.id
      delete partner.id

      let result = await fetchData(
        urls.partnerStatuses + `/${id}`,
        'POST',
        this.bodyToSave(partner)
      )
      if (!result?.detail) {
        let partnerRow = this.rows.find((item) => item.id === id)
        for (const key in partner) {
          if (key in partnerRow) partnerRow[key] = partner[key]
        }

        this.isHideNewRowComponent = true
        this.$nextTick(() => {
          this.isHideNewRowComponent = false
        })
      } else console.error(result.detail)

      this.$store.commit('setIsLoading', false)
    },

    async addRow() {
      this.$store.commit('setIsLoading', true)

      let result = await fetchData(
        urls.partnerStatusesCreate,
        'POST',
        this.bodyToSave(this.newPartner)
      )
      if (!result?.detail) {
        const tmpPartner = this.newPartner
        this.rows.unshift({ ...{ id: result.id }, ...tmpPartner })
        this.isHideNewRowComponent = true
        this.$nextTick(() => {
          this.isHideNewRowComponent = false
        })

        this.newPartnerToDefault()
      } else {
        this.isHideNewRowComponent = false
        console.error(result.detail)
      }

      this.$store.commit('setIsLoading', false)
    },

    async deleteRow(id) {
      this.$store.commit('setIsLoading', true)

      let result = await fetchData(urls.partnerStatuses + `/${id}`, 'DELETE')
      if (!result?.detail) {
        const idx = this.rows.findIndex((item) => item.id === id)
        this.rows.splice(idx, 1)
      } else console.error(result.detail)

      this.$store.commit('setIsLoading', false)
    },

    async fetchData() {
      this.setQueryParams()

      if (this.isLoading) return

      this.$store.commit('setIsLoading', true)
      let query = `page=${this.page}&per_page=${this.per_page}`
      let prevSearch_text = this.search_text.value
      if (this.search_text.value?.length > 2) {
        query += `&search_text=${this.search_text.value}`
      }

      query += this.queryToSort()

      let result = await fetchData(urls.partnerStatuses, 'GET', null, query)

      this.rows = (result?.rows || []).map((item) => {
        return {
          id: item.id,
          partner_status: item.partner_status,
        }
      })

      this.countRows = result?.count || 0
      this.search_textToGrid = this.search_text.value
      this.$store.commit('setIsLoading', false)

      if (
        this.search_text.value?.length > 2 &&
        prevSearch_text !== this.search_text.value
      )
        this.fetchData()
    },

    setFilters() {
      this.isVisibleFilters = !this.isVisibleFilters
    },
  },
}
</script>
